import React, { useMemo } from 'react';
import styles from './ProposalTitle.module.css';
import { slugify } from '../../../parser/proposalToWebData';
import { sanitizeRichHtml } from '../../../utils/sanitizeRichHtml';
import {
  buildWebTitleStyle,
  configurationFromProjectConfig,
  headingBackgroundOverrideCss,
  webTextStyleToInlineCss,
} from '../../../utils/proposalTextStyle';
import { buildTitleBlockLayoutCss, computeTitleIndentPx } from '../../../utils/proposalTitleLayout';
import { useProposalProjectConfig } from '../../../contexts/ProposalProjectConfigContext';
import ProposalBoxAlign from '../ProposalBoxAlign/ProposalBoxAlign';
import ProposalTitleBubbleEditor from './ProposalTitleBubbleEditor';

function normalizeTitleHtml(html, titleLevel) {
  if (!html || ![1, 2, 3, 4].includes(titleLevel)) return html || '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const tag = `h${titleLevel}`;
    const first = doc.body.firstElementChild;
    if (first && first.tagName.toLowerCase() !== tag) {
      const heading = doc.createElement(tag);
      heading.innerHTML = first.innerHTML;
      first.replaceWith(heading);
    } else if (!first) {
      const heading = doc.createElement(tag);
      doc.body.appendChild(heading);
    }

    if (titleLevel === 2) {
      const h2s = Array.from(doc.querySelectorAll('h2'));
      const used = new Map();
      h2s.forEach((h2) => {
        if (h2.id) return;
        const base = slugify(h2.textContent || 'section');
        const count = used.get(base) || 0;
        used.set(base, count + 1);
        h2.id = count === 0 ? base : `${base}-${count + 1}`;
      });
    }

    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

/**
 * Bloc Puck — Titre proposal (niveaux 1–4).
 */
const ProposalTitle = ({
  id,
  html = '',
  titleLevel = 1,
  indexTitle = null,
  textStyle = null,
  drawMode = false,
  alignHorizontal = 'end',
  alignVertical = 'center',
  isVisible = true,
  /* La configuration des titres du document peut poser un fond (le jaune des
     titres de niveau 2, par exemple). Sans réglage ici, il n'était modifiable
     nulle part. Vide = on garde celui du document. */
  backgroundColor = '',
  puck,
}) => {
  const isPuckEditing = !!puck?.isEditing;
  const projectConfig = useProposalProjectConfig();

  const resolvedTextStyle = useMemo(() => {
    const styleOptions = {
      drawMode,
      indexTitle,
      indentFromLevelOnly: true,
    };
    const fromProject = configurationFromProjectConfig(projectConfig);
    if (fromProject) {
      return buildWebTitleStyle(fromProject, titleLevel, styleOptions) || textStyle;
    }
    if (textStyle) {
      const indentPx = computeTitleIndentPx(
        { indentationTitles: projectConfig?.indentationTitles ?? 0 },
        titleLevel,
        indexTitle,
        { fromLevelOnly: true },
      );
      return { ...textStyle, indentPx, titleLevel };
    }
    return null;
  }, [projectConfig, titleLevel, indexTitle, drawMode, textStyle]);

  /* 'transparent' efface le fond du document ; une couleur le remplace. */
  const styledText = useMemo(() => {
    if (!backgroundColor) return resolvedTextStyle;
    return {
      ...(resolvedTextStyle || {}),
      backgroundColor: backgroundColor === 'transparent' ? null : backgroundColor,
    };
  }, [resolvedTextStyle, backgroundColor]);

  const htmlWithHeading = useMemo(
    () => (isVisible ? normalizeTitleHtml(html, titleLevel) : ''),
    [html, titleLevel, isVisible],
  );

  /* Idem : la variable `--proposal-hN-bg-color` peint le titre lui-même. */
  const inlineStyle = {
    ...webTextStyleToInlineCss(styledText, { drawMode }),
    ...(headingBackgroundOverrideCss(backgroundColor) || {}),
  };
  const indentPx = styledText?.indentPx ?? 0;
  const blockLayoutStyle = buildTitleBlockLayoutCss({
    indentPx,
    horizontal: alignHorizontal,
    drawMode,
  });

  const titleClassName = `${styles.title} ${styledText ? styles.boxStyled : ''}`.trim();
  const mergedStyle = { ...inlineStyle, ...blockLayoutStyle };

  if (isPuckEditing) {
    return (
      <ProposalTitleBubbleEditor
        id={id}
        puck={puck}
        // Le niveau choisi dans l'inspecteur doit se voir à l'édition aussi :
        // un <h2> resté tel quel garderait le style du titre 2.
        html={normalizeTitleHtml(html, titleLevel)}
        titleLevel={titleLevel}
        textStyle={styledText}
        drawMode={drawMode}
        alignVertical={alignVertical}
        blockLayoutStyle={blockLayoutStyle}
        className={titleClassName}
        style={inlineStyle}
      />
    );
  }

  if (!html || !isVisible) return null;

  const content = (
    <div
      id={id}
      className={titleClassName}
      style={mergedStyle}
      dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(htmlWithHeading) }}
    />
  );

  if (drawMode) return content;

  return (
    <ProposalBoxAlign horizontal="start" vertical={alignVertical} innerMode="full">
      {content}
    </ProposalBoxAlign>
  );
};

export default ProposalTitle;
